/**
 * Voice-to-transaction: "₹450 chai mausi ke saath" → structured Transaction.
 *
 * Tries deterministic regex first (works offline, no LLM), then LLM refinement
 * if regex fails or confidence is low.
 */
import { captureOnce } from '../chassis/voice.ts';
import { generate } from '../chassis/llm.ts';
import type { Category, Transaction } from '../types.ts';
import { CATEGORIES } from '../types.ts';

const HINDI_NUMBER_WORDS: Record<string, number> = {
  'das': 10,
  'bees': 20,
  'tees': 30,
  'chalis': 40,
  'pachas': 50,
  'sau': 100,
  'so': 100,
  'hazaar': 1000,
  'lakh': 100_000,
};

function parseHindiNumbers(text: string): number | null {
  let total = 0;
  let cur = 0;
  let matched = false;
  for (const w of text.toLowerCase().split(/\s+/)) {
    const n = HINDI_NUMBER_WORDS[w];
    if (typeof n === 'number') {
      matched = true;
      if (n >= 100) {
        cur = (cur === 0 ? 1 : cur) * n;
        total += cur;
        cur = 0;
      } else {
        cur += n;
      }
    }
  }
  total += cur;
  return matched && total > 0 ? total : null;
}

const AMOUNT_RE = /(?:rs\.?|inr|₹|rupees?)?\s*([\d,]+(?:\.\d{1,2})?)/i;

function parseAmount(text: string): number | null {
  const t = text.toLowerCase();
  const hindi = parseHindiNumbers(t);
  if (hindi !== null) return hindi;
  const m = t.match(AMOUNT_RE);
  if (m === null) return null;
  const n = Number(m[1]?.replace(/,/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

const CATEGORY_KEYWORDS: Record<Category, string[]> = {
  food_delivery: ['chai', 'coffee', 'lunch', 'dinner', 'breakfast', 'swiggy', 'zomato', 'biryani', 'pizza', 'restaurant', 'cafe', 'khana'],
  grocery: ['sabzi', 'subzi', 'kirana', 'grocery', 'rice', 'dal', 'atta', 'milk', 'doodh', 'zepto', 'blinkit'],
  fashion: ['shirt', 'kurta', 'saree', 'jeans', 'shoes', 'myntra', 'ajio'],
  beauty: ['makeup', 'parlor', 'salon', 'nykaa', 'cream'],
  electronics: ['laptop', 'phone', 'iphone', 'mobile', 'charger'],
  lifestyle: ['gift', 'amazon', 'flipkart'],
  travel: ['ticket', 'flight', 'train', 'hotel', 'airbnb'],
  entertainment: ['movie', 'cinema', 'netflix', 'spotify', 'concert'],
  health: ['medicine', 'doctor', 'hospital', 'dawai', 'pharmacy'],
  home: ['rent', 'electricity', 'water', 'gas'],
  mobility: ['uber', 'ola', 'auto', 'rapido', 'petrol', 'diesel', 'fuel', 'parking', 'cab'],
  services: ['internet', 'jio', 'airtel', 'subscription'],
  other: [],
};

function pickCategory(text: string): Category {
  const lower = text.toLowerCase();
  for (const cat of CATEGORIES) {
    const kws = CATEGORY_KEYWORDS[cat];
    if (kws.some((k) => lower.includes(k))) return cat;
  }
  return 'other';
}

function extractPerson(text: string): string | null {
  const lower = text.toLowerCase();
  const patterns = [
    /(?:with|saath|sath|ke saath)\s+([a-zऀ-ॿ]+)/i,
    /(?:for|liye|ke liye)\s+([a-zऀ-ॿ]+)/i,
  ];
  for (const p of patterns) {
    const m = lower.match(p);
    if (m?.[1] !== undefined) return m[1];
  }
  return null;
}

export interface VoiceDraft {
  readonly amount_inr: number;
  readonly category: Category;
  readonly note: string;
  readonly party: string | null;
  readonly confidence: number;
  readonly transcript: string;
}

export async function voiceToDraft(lang: 'en-IN' | 'hi-IN' = 'en-IN'): Promise<VoiceDraft> {
  const cap = await captureOnce(lang);
  return parseDraft(cap.text, cap.confidence);
}

export function parseDraft(transcript: string, capConfidence: number = 0.7): VoiceDraft {
  const amount = parseAmount(transcript);
  return {
    amount_inr: amount ?? 0,
    category: pickCategory(transcript),
    note: transcript,
    party: extractPerson(transcript),
    confidence: amount === null ? 0.2 : Math.min(0.92, capConfidence),
    transcript,
  };
}

export async function llmRefineVoice(draft: VoiceDraft): Promise<VoiceDraft> {
  if (draft.confidence >= 0.8) return draft;
  const prompt = `Parse this voice spending note into JSON {amount: number (INR), category: one of [${CATEGORIES.join(', ')}], party: string or null}. Reply only with the JSON.\nNote: "${draft.transcript}"\nJSON:`;
  const out = await generate(prompt, { maxTokens: 80, temperature: 0 });
  const m = out.match(/\{[\s\S]*\}/);
  if (m === null) return draft;
  try {
    const parsed = JSON.parse(m[0]) as { amount?: number; category?: Category; party?: string | null };
    return {
      ...draft,
      amount_inr: typeof parsed.amount === 'number' ? parsed.amount : draft.amount_inr,
      category: typeof parsed.category === 'string' && (CATEGORIES as readonly string[]).includes(parsed.category) ? parsed.category : draft.category,
      party: parsed.party ?? draft.party,
      confidence: 0.92,
    };
  } catch {
    return draft;
  }
}

export function draftToTransaction(draft: VoiceDraft): Transaction {
  const id = `v-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return {
    id,
    source: 'manual',
    source_ref: 'voice',
    merchant_id: draft.party ?? 'voice_entry',
    merchant_display: draft.party ?? 'Voice note',
    amount_inr: draft.amount_inr,
    items: [draft.note],
    category: draft.category,
    date: new Date().toISOString().slice(0, 10),
    confidence: draft.confidence,
    detected_at: new Date().toISOString(),
  };
}
